/* eslint-disable max-len */
import React, { useEffect, useMemo } from 'react'

import { graphql, Link } from 'gatsby'
import htmr from 'htmr'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import Breadcrumbs from '../components/core/Breadcrumbs'
import Layout from '../components/core/layout'
import Seo from '../components/core/seo'
import ChevronBackgroundTwoTone from '../components/elements/ChevronBackgroundTwoTone'
import Footnote from '../components/elements/Footnote'
import HeroInternal from '../components/elements/HeroInternal'
import MarkdownContent from '../components/elements/MarkdownContent'
import TwoColWithBorder from '../components/elements/TwoColWithBorder'
import FormNoCampus from '../components/forms/FormNoCampus'
import FormWrapperGray from '../components/forms/FormWrapperGray'
import TestimonialSlider from '../components/reusables/TestimonialSlider'
import TextBoxOverImage from '../components/reusables/TextBoxOverImage'
import VideoLazyload from '../components/reusables/VideoLazyload'
import YouTubeEmbed from '../components/reusables/YouTubeEmbed'
import Chevron from '../components/uma-svgs/Chevron'
import { campusEnums, personaEnums, programEnums } from '../enums'
import { useCampus, useNextStartDate, usePersona, useProgram } from '../hooks'
import { ProgramModel, TestimonialVideoModel } from '../models'
import { colors, space } from '../style/design-system'
import { programTypes, testimonialTypes } from '../types'
import bls from '../utils/bls-citations'
import sp from '../utils/filters/shortcodeParser'
import { formatPhoneNumber } from '../utils/phoneNumberFormatter'
import { phoneNumbers } from '../utils/static-data/phoneNumbers'
import himJson from '../utils/static-data/schemaMarkup/programs/him.json'
import { studentInfo } from '../utils/static-data/studentInfo'

type Props = {
  className?: string
  data: {
    core: programTypes.Core
    markdown: programTypes.Markdown
    testimonials: {
      edges: { node: testimonialTypes.TestimonialVideo }[]
    }
  }
}

const DefaultProps = {
  className: '',
}

const ProgramSingle = ({ className, data }: Props) => {
  const nextStartDate = useNextStartDate()

  const program: programTypes.Program = useMemo(() => {
    return new ProgramModel({
      code: data.core.metaProgramCode,
      core: data.core,
      markdown: data.markdown,
    })
  }, [data])
  const testimonials: { node: testimonialTypes.TestimonialVideo }[] = data.testimonials.edges.map((t) => {
    const testimonial = new TestimonialVideoModel(t.node)
    return { node: testimonial }
  })

  const { resetCampus, setCampus } = useCampus()
  const { resetPersona, setPersona } = usePersona()
  const { resetProgram, setProgram } = useProgram()
  useEffect(() => {
    setCampus(program.core.metaProgramCampus)
    setProgram(program)
    if (program.core.metaProgramCampus === campusEnums.codes.CLEARWATER) {
      setPersona(personaEnums.personas.PROGRAM_CLEARWATER)
    } else if (program.core.metaProgramCampus === campusEnums.codes.ONLINE) {
      setPersona(personaEnums.personas.PROGRAM_ONLINE)
    }
    return () => {
      resetCampus()
      resetPersona()
      resetProgram()
    }
  }, [program, resetCampus, resetPersona, resetProgram, setCampus, setPersona, setProgram])

  const bottomPay = bls?.[program.core.metaProgramCode.toLowerCase()]?.payBottom?.number
  const medianPay = bls?.[program.core.metaProgramCode.toLowerCase()]?.payMedian?.number
  const topPay = bls?.[program.core.metaProgramCode.toLowerCase()]?.payTop?.number

  const healthcareJobsNum = bls.healthcare.growthJobsInternal.number
  const healthcareJobsToDate = bls.global.toDate

  return (
    <Layout>
      <Seo
        useUMAAcronym={program.core.metaSeoTitleUseAcronym}
        title={program.core.metaSeoTitle}
        description={program.core.metaSeoMetadesc}
        ogImage={program.markdown.frontmatter.hero.img.publicURL}>
        {program.core.metaProgramCode === programEnums.codes.HIM_DEGREE && (
          <script type="application/ld+json">{`${JSON.stringify(himJson)}`}</script>
        )}
      </Seo>

      <div className={className}>
        <HeroInternal
          backgroundImage={{
            image: program.markdown.frontmatter.hero.img.childImageSharp.gatsbyImageData,
            objectPosition: program.markdown.frontmatter.hero.imgPosition || '50% 50%',
          }}
          backgroundImageMobile={{
            image: program.markdown.frontmatter.hero.img.childImageSharp.gatsbyImageData,
            objectPosition: program.markdown.frontmatter.hero.imgPositionMobile || '50% top',
          }}
          contentLg={sp(program.markdown.frontmatter.hero.heading || program.core.metaProgramFullName)}
          contentSm={sp(program.markdown.frontmatter.desc)}
          invertImg={program.markdown.frontmatter.hero.imgInvert || false}
          alt={program.markdown.frontmatter.hero.imgAlt || ''}
        />

        <main>
          <div className="section--breadcrumbs">
            <div className="container text-lg-center">
              <Breadcrumbs
                customCrumbs={[
                  {
                    crumbLabel: 'Home',
                    pathname: '/',
                  },
                  {
                    crumbLabel: 'Programs',
                    pathname: '/future-students/programs',
                  },
                  {
                    crumbLabel: program.core.metaProgramShortName,
                    pathname: program.core.uri,
                  },
                ]}
              />
            </div>
          </div>

          <div className="section--degree pb-6 pb-lg-10">
            <div className="container">
              <div className="row">
                <div className="d-lg-none col-12">
                  <h1 className="h1 d-lg-none mt-0 mb-6">
                    {htmr(sp(program.markdown.frontmatter.hero.heading || program.core.metaProgramFullName))}
                  </h1>
                  <p>{htmr(sp(program.markdown.frontmatter.desc))}</p>
                </div>
                <div className="col-lg-10 offset-lg-1">
                  <MarkdownContent content={program.markdown.frontmatter.section_degree.content} />
                </div>
              </div>
            </div>
          </div>

          <div className="section--about py-9 py-lg-12">
            <div className="container">
              <div className="row d-flex align-items-center">
                <div className="col-12 col-lg-6 order-2 order-lg-1">
                  <MarkdownContent content={program.markdown.frontmatter.section_about.content} />
                </div>
                <div className="col-12 col-lg-6 order-1 order-lg-2 mb-8 mb-lg-0">
                  <VideoLazyload
                    displayPlayIcon
                    posterAlt={program.core.metaProgramShortName}
                    posterGatsbyImg={{
                      image: program.markdown.frontmatter.section_about.video.posterSrc.childImageSharp.gatsbyImageData,
                    }}>
                    <YouTubeEmbed
                      autoplay
                      title={`About UMA's ${program.core.metaProgramShortName} Program`}
                      videoId={program.markdown.frontmatter.section_about.video.videoId}
                    />
                  </VideoLazyload>
                </div>
              </div>
            </div>
          </div>

          {program.core.metaProgramCampus === '45' &&
            program.core.metaProgramIsEnrolling !== '' &&
            nextStartDate !== null && (
              <div className="bg-dark-blue py-7">
                <h2 className="white m-0 text-center">Classes start {nextStartDate}</h2>
              </div>
            )}

          <FormWrapperGray bgColor={colors.dev.blue.d()} textColor="#fff">
            <div className="container">
              <FormNoCampus includeUserErrorModal className="inline-form" />
            </div>
          </FormWrapperGray>

          <div className="section--training py-9 py-lg-12 list-style-ascii-chevron">
            <TwoColWithBorder
              borderAt={6}
              content1={() => <MarkdownContent content={program.markdown.frontmatter.section_training.content1} />}
              content2={() => <MarkdownContent content={program.markdown.frontmatter.section_training.content2} />}
            />
          </div>

          <TextBoxOverImage
            gatsbyImage={{
              image: program.markdown.frontmatter.section_work_environment.img.childImageSharp.gatsbyImageData,
              objectPosition: program.markdown.frontmatter.section_work_environment.backgroundPosition
                ? program.markdown.frontmatter.section_work_environment.backgroundPosition
                : 'center center',
            }}
            gatsbyImageMobile={{
              image: program.markdown.frontmatter.section_work_environment.img.childImageSharp.gatsbyImageData,
              objectPosition: program.markdown.frontmatter.section_work_environment.backgroundPositionMobile
                ? program.markdown.frontmatter.section_work_environment.backgroundPositionMobile
                : 'center center',
            }}
            bootstrapClasses={
              program.markdown.frontmatter.section_work_environment.contentPositioning
                ? program.markdown.frontmatter.section_work_environment.contentPositioning
                : 'col-12 col-lg-6 offset-lg-3'
            }
            className="text-white-force list-style-ascii-chevron py-lg-12">
            <MarkdownContent
              content={program.markdown.frontmatter.section_work_environment.content}
              className="w-75 m-auto"
            />
          </TextBoxOverImage>

          <div className="section--why-choose list-style-ascii-chevron py-9 py-lg-12">
            <div className="container">
              <div className="h2 mt-0 mb-8">
                <MarkdownContent content={program.markdown.frontmatter.section_why_choose.heading} />
              </div>
            </div>
            <TwoColWithBorder
              borderAt={5}
              content1={() => (
                <MarkdownContent content={program.markdown.frontmatter.section_why_choose.content_left} />
              )}
              content2={() => (
                <>
                  <p>
                    At UMA, you won’t feel like just another number. In fact, you’ll participate in classrooms with a
                    maximum size of 25 students. Our student support teams can help you with academic support, technical
                    assistance, job search guidance, mock interviews, financial aid if qualified, and more.
                  </p>
                  <p>
                    Our programs also include 24/7 tech support that helps you become comfortable with computers and in
                    the UMA online environment. Receive guidance from your instructors and learn what it takes to pursue
                    your career.
                  </p>
                  <MarkdownContent content={program.markdown.frontmatter.section_why_choose.content_right} />
                </>
              )}
            />
          </div>

          {program.core.metaProgramCampus === campusEnums.codes.ONLINE && (
            <div className="section--continuing-ed">
              <ChevronBackgroundTwoTone>
                <div className="container text-white-force text-center">
                  <div className="row">
                    <div className="col-10 offset-1">
                      <div className="h2">Continuing Education After UMA</div>
                      <p>
                        Your associate degree doesn’t need to be the end of your education; UMA maintains agreements
                        with various educational partners who share our values and our missions. Through these
                        partnerships, alumni with an associate degree from UMA who meet a partner’s admissions
                        requirements have an opportunity to pursue bachelor’s and master’s degrees in select areas of
                        study.
                        <Footnote
                          content={`{{
                        "shortcode": "footnote",
                        "content": "Select credits may transfer at the discretion of the receiving institution."}}`}
                        />{' '}
                        Find more information on UMA’s{' '}
                        <Link className="underline white" to="/students/uma-partner-schools/">
                          partner schools
                        </Link>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </ChevronBackgroundTwoTone>
            </div>
          )}

          {program.core.metaProgramCode !== programEnums.codes.NA_DIPLOMA &&
            program.core.metaProgramCode !== programEnums.codes.PH_DIPLOMA && (
              <div className="section--tuition list-style-ascii-chevron py-9 py-lg-12">
                <TwoColWithBorder
                  borderAt={7}
                  content1={() => (
                    <>
                      <h2 className="mt-0 mb-5">What kind of financial aid options are available?</h2>
                      <p>
                        If you qualify for applicable financial aid, we’ll walk you through the process. Multiple
                        resources may be available to help pay for the cost of your education. Our Student Finance
                        advisors are committed to helping you understand the options you qualify for and can assist you
                        in applying for various forms of financial aid to see if you qualify.
                      </p>
                      <p>
                        To learn more about financial aid possibilities, contact a Student Finance advisor at&nbsp;
                        {(() =>
                          program.core.metaProgramCampus === '47' ? (
                            <a className="nowrap" href={`tel:${phoneNumbers.umaClearwater}`}>
                              {formatPhoneNumber(phoneNumbers.umaClearwater)}
                            </a>
                          ) : (
                            <a className="nowrap" href={`tel:${phoneNumbers.umaFinancialAid}`}>
                              {formatPhoneNumber(phoneNumbers.umaFinancialAid)}
                            </a>
                          ))()}
                        .
                      </p>
                    </>
                  )}
                  content2={() => (
                    <>
                      <h2 className="mt-0 mb-5">Not sure which program to choose?</h2>
                      <p>
                        Try our fun and informational <Link to="/program-quiz">healthcare careers quiz</Link> for
                        inspiration. Just answer a few simple personality questions and learn which program may best
                        suit you!
                      </p>
                    </>
                  )}
                />
              </div>
            )}

          <TestimonialSlider testimonials={testimonials} heading="In their words&#8230;" />

          {/* // ** We are currently not placing the Program Overviews on the site.
        <ChevronBackgroundStroke className="section--discover py-9 py-lg-12" direction="north">
          <div className="container text-center">
            <div className="h2 mt-0">Discover a new career path today!</div>
            <p>
              Find out how earning a {program.core.metaProgramFullName}
              {program.core.metaProgramType === 'degree' ? ' degree' : ' diploma'}
              can change your life.
            </p>
            <a className="btn btn-outline-black" href="#">Request Your Guide&nbsp;&gt;</a>
          </div>
        </ChevronBackgroundStroke> */}

          {program.markdown.frontmatter.section_faq && program.markdown.frontmatter.section_faq.faqs.length > 0 && (
            <ChevronBackgroundTwoTone chevron={{ display: false }}>
              <div className="container text-white-force faq">
                <div className="row">
                  <div className="col-lg-8 offset-lg-2">
                    <h2>FAQs</h2>
                    <ul className="list-style-none">
                      {program.markdown.frontmatter.section_faq.faqs.map((faq) => (
                        <li key={uuidv4()} className="my-9 my-lg-10">
                          <h3 className="my-0 mb-6 d-inline-block text-align-baseline">
                            <Chevron
                              width="32px"
                              direction="south"
                              color={colors.dev.green.e()}
                              className="d-inline-block mr-6 valign-baseline"
                            />
                            <span className="valign-baseline">{faq.q}</span>
                          </h3>
                          <MarkdownContent content={faq.a} className="list-style-ascii-chevron" />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </ChevronBackgroundTwoTone>
          )}
        </main>
      </div>
    </Layout>
  )
}

ProgramSingle.defaultProps = DefaultProps

export default styled(ProgramSingle)`
  .section--degree,
  .section--breadcrumbs {
    background-color: ${colors.dev.gray.h()};
  }

  .section--job-outlook {
    .box {
      border: 1px solid ${colors.dev.gray.d()};
      border-radius: 2px;
      padding: ${space.d};

      .h1 {
        text-transform: capitalize;
      }
    }
  }

  .section--stats {
    .bell-curve {
      padding: 35px 0 35px 0;
      position: relative;
      text-align: center;

      .bell-curve-img {
        padding: 41px 0;
      }

      img {
        width: 100%;
      }

      .bell-curve-text {
        display: flex;
        justify-content: space-between;
        margin: 0 auto;
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        width: 95%;
      }

      .c1,
      .c2,
      .c3 {
        align-items: center;
        display: flex;
        flex: 0 0 15%;
        flex-direction: column;
        justify-content: space-between;

        .middle {
          border: 2px dashed #fff;
          height: 70%;
        }
      }
    }
  }

  .no-longer-enrolling {
    background-color: ${colors.umaLightGray};
  }

  .underline {
    text-decoration: underline;
    &.white {
      &:hover {
        color: #fff;
      }
    }
  }

  .faq {
    a {
      text-decoration: underline;
      &:hover {
        color: #fff;
      }
    }
  }

  h2,
  .h2 {
    margin-top: 0;
    //margin-bottom: ${space.e};
  }
  h3,
  .h3 {
    //margin-bottom: ${space.d};
  }
`

export const query = graphql`
  query ($id: String!, $programCodeRegex: String!) {
    core: wpUmaprogram(id: { eq: $id }) {
      ...programAPI
    }
    markdown: markdownRemark(frontmatter: { programCode: { regex: $programCodeRegex } }) {
      frontmatter {
        ...programMarkdown
      }
    }
    testimonials: allWpTestimonial(
      filter: { uri: { regex: "/leticia-b|jennifer-w|dominique-b|joshua-a|angelica-v|miguel-r|heather-r|brittney-j/" } }
    ) {
      edges {
        node {
          ...testimonial
        }
      }
    }
  }
`
